import React, { useState, useEffect, useCallback, useRef } from "react";
import { authFetch } from "../lib/api";
import { Send, MessageSquare, Calendar, ChevronDown } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import useAuth from "../hooks/useAuth";
import { Icons } from "../components/icons";

// ─── Helpers ───────────────────────────────────────────────────────────────
const getInitials = (name = "") =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const getMonday = (d = new Date()) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
};

const toDateKey = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const formatHeaderDate = (dateStr) => {
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleDateString("en-US", { month: "short" });
  const week = d.toLocaleDateString("en-US", { weekday: "short" });
  return { day: `${day} ${month}`, weekday: week };
};

// ─── Comment Modal ─────────────────────────────────────────────────────────
const CommentModal = ({
  taskId,
  taskName,
  commentCount: initCount,
  isSubmitted,
  onClose,
}) => {
  const { user: currentUser } = useAuth();
  const [comments, setComments] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [, setCount] = useState(initCount);
  const [activeMenu, setActiveMenu] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const bottomRef = useRef(null);

  const fetchComments = useCallback(async () => {
    try {
      const res = await authFetch(`/api/tasks/${taskId}/comments`);
      const json = await res.json();
      if (json?.data) {
        setComments(json.data);
        setCount(json.commentCount);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments]);

  const handlePost = async () => {
    if (!input.trim() || posting || isSubmitted) return;
    setPosting(true);
    try {
      const res = await authFetch(`/api/tasks/${taskId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: input.trim() }),
      });
      const json = await res.json();
      if (res.ok && json?.data) {
        setComments((prev) => [...prev, json.data]);
        setCount(json.commentCount);
        setInput("");
      }
    } catch {
    } finally {
      setPosting(false);
    }
  };

  const handleEditClick = (comment) => {
    setEditingId(comment._id);
    setEditingText(comment.text);
    setActiveMenu(null);
  };

  const handleSaveEdit = async (commentId) => {
    if (!editingText.trim()) return;
    try {
      const res = await authFetch(
        `/api/tasks/${taskId}/comments/${commentId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: editingText.trim() }),
        },
      );
      const json = await res.json();
      if (res.ok && json?.data) {
        setComments((prev) =>
          prev.map((c) => (c._id === commentId ? json.data : c)),
        );
      }
    } catch {
    } finally {
      setEditingId(null);
      setEditingText("");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white w-[95vw] max-w-[500px] rounded-xl relative max-h-[90vh] flex flex-col overflow-hidden shadow-xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
        >
          ✕
        </button>
        <div className="p-6 border-b">
          <h2 className="text-base font-bold text-gray-800 border-l-4 border-blue-500 pl-3">
            Comments
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Task: <span className="font-medium text-gray-700">{taskName}</span>
          </p>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-[180px]">
          {loading ? (
            <p className="text-sm text-gray-400 text-center py-8">Loading...</p>
          ) : comments.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">
              No comments yet. Be the first to comment!
            </p>
          ) : (
            comments.map((c, i) => {
              const isOwn =
                currentUser?._id &&
                (c.author?._id || c.author) === currentUser._id;
              return (
                <div key={c._id || i} className="flex gap-3">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {getInitials(c.author?.name || "You")}
                  </div>
                  <div className="flex-1 min-w-0 bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between items-start">
                      <span className="font-semibold text-sm text-gray-800">
                        {c.author?.name || "You"}
                      </span>
                      <span className="text-xs text-gray-400">
                        {c.createdAt
                          ? new Date(c.createdAt).toLocaleString()
                          : ""}
                      </span>
                    </div>
                    {editingId === c._id ? (
                      <div className="mt-2">
                        <textarea
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          className="w-full border rounded-md p-2 text-sm resize-none"
                          rows={3}
                        />
                        <div className="flex justify-end gap-2 mt-2">
                          <button
                            className="px-3 py-1 border rounded-md text-sm"
                            onClick={() => {
                              setEditingId(null);
                              setEditingText("");
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            className="px-3 py-1 bg-blue-500 text-white rounded-md text-sm"
                            onClick={() => handleSaveEdit(c._id)}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-700 mt-1">{c.text}</p>
                    )}
                  </div>
                  {isOwn && !isSubmitted && editingId !== c._id && (
                    <div className="relative flex-shrink-0">
                      <button
                        className="text-gray-500 px-2"
                        onClick={() =>
                          setActiveMenu(activeMenu === c._id ? null : c._id)
                        }
                      >
                        ⋯
                      </button>
                      {activeMenu === c._id && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setActiveMenu(null)}
                          />
                          <div className="absolute right-0 top-6 bg-white border rounded-md shadow-lg z-50 w-[100px]">
                            <button
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                              onClick={() => handleEditClick(c)}
                            >
                              Edit
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>
        {!isSubmitted && (
          <div className="border-t p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                Y
              </div>
              <div className="flex-1 flex items-center border border-gray-200 rounded-lg focus-within:border-blue-400 overflow-hidden">
                <input
                  type="text"
                  className="flex-1 outline-none text-sm px-3 py-2 bg-transparent"
                  placeholder="Write your comment..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handlePost();
                    }
                  }}
                />
              </div>
              <button
                onClick={handlePost}
                disabled={posting || !input.trim()}
                className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition disabled:opacity-40"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Timesheet Component ───────────────────────────────────────────────
const Timesheet = () => {
  const [timesheetData, setTimesheetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [editingCell, setEditingCell] = useState(null); // "entryId-dateKey"
  const [editValue, setEditValue] = useState("");
  const [cellError, setCellError] = useState({}); // { "entryId-dateKey": "msg" }
  const [commentModal, setCommentModal] = useState(null); // { taskId, taskName, commentCount }
  const [weekOffset] = useState(0);
  const focusEditInput = useCallback((node) => {
    if (node) node.focus({ preventScroll: true });
  }, []);

  const monday = getMonday(anchorDate);
  const weekEnd = new Date(monday);
  weekEnd.setDate(monday.getDate() + 6);
  const startDateStr = toDateKey(monday);

  const formatRangeLabel = (start, end) => {
    const opts = { day: "2-digit", month: "short" };
    const startLabel = start.toLocaleDateString("en-US", opts);
    const endLabel = end.toLocaleDateString("en-US", {
      ...opts,
      year: "numeric",
    });
    return `${startLabel} – ${endLabel}`;
  };

  // ── Fetch timesheet ──────────────────────────────────────────────────────
  const fetchTimesheet = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch(`/api/timesheets?startDate=${startDateStr}`);
      const json = await res.json();
      if (json?.data) setTimesheetData(json.data);
      else setError(json?.message || "Failed to load timesheet.");
    } catch {
      setError("Network error.");
    } finally {
      setLoading(false);
    }
  }, [startDateStr]);

  useEffect(() => {
    fetchTimesheet();
  }, [fetchTimesheet]);

  // ── Auto-submit scheduler (frontend fallback — backend cron is primary) ──
  useEffect(() => {
    if (!timesheetData || timesheetData.status === "submitted") return;
    const now = new Date();
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 0, 0);
    const msUntilSunday = sunday.getTime() - now.getTime();
    if (msUntilSunday <= 0) return;
    const timer = setTimeout(() => {
      handleSubmit(true);
    }, msUntilSunday);
    return () => clearTimeout(timer);
  }, [timesheetData, monday]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save hours cell ──────────────────────────────────────────────────────
  const handleCellBlur = async (
    entryId,
    dateKey,
    value,
    cellKey,
    pIdx,
    eIdx,
  ) => {
    setEditingCell(null);
    const hours = parseFloat(value);
    if (isNaN(hours) || hours < 0) return;

    // Daily total validation — sum hours of all OTHER cells for this day,
    // excluding the exact cell being edited (identified by project+entry index)
    const otherEntriesTotal = (timesheetData?.projects || []).reduce(
      (sum, p, pi) =>
        sum +
        (p.entries || []).reduce((eSum, e, ei) => {
          if (pi === pIdx && ei === eIdx) return eSum;
          const log = (e.logs || []).find((l) => l.dateKey === dateKey);
          return eSum + (log && !log.isLeave ? Number(log.hours) || 0 : 0);
        }, 0),
      0,
    );

    if (otherEntriesTotal + hours > 9) {
      setCellError((prev) => ({ ...prev, [cellKey]: `Maximum 9h Per Day` }));
      setTimeout(
        () =>
          setCellError((prev) => {
            const n = { ...prev };
            delete n[cellKey];
            return n;
          }),
        3000,
      );
      return;
    }

    setSaving(true);
    try {
      let res, json;
      if (timesheetData._id) {
        res = await authFetch(
          `/api/timesheets/${timesheetData._id}/entries/${entryId}/logs`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              date: dateKey,
              hours,
            }),
          },
        );
        json = await res.json();
      } else {
        // No timesheet yet — create it. project/task can arrive populated
        // (objects) or as plain ids depending on the scaffold response.
        const allEntries = (timesheetData.projects || []).flatMap((p, pi) =>
          (p.entries || []).map((e, ei) => ({
            project: e.project?._id || e.project,
            task: e.task?._id || e.task,
            logs: (e.logs || []).map((l) => ({
              date: l.dateKey || toDateKey(l.date),
              hours:
                (l.dateKey || toDateKey(l.date)) === dateKey &&
                pi === pIdx &&
                ei === eIdx
                  ? hours
                  : Number(l.hours) || 0,
              isLeave: l.isLeave || false,
            })),
          })),
        );
        res = await authFetch("/api/timesheets", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            startDate: startDateStr,
            endDate: toDateKey(new Date(monday.getTime() + 6 * 86400000)),
            entries: allEntries,
          }),
        });
        json = await res.json();
      }

      if (res.ok && json?.data) {
        setTimesheetData(json.data);
        setCellError((prev) => {
          const n = { ...prev };
          delete n[cellKey];
          return n;
        });
      } else {
        setCellError((prev) => ({
          ...prev,
          [cellKey]: json?.message || "Error saving",
        }));
        setTimeout(
          () =>
            setCellError((prev) => {
              const n = { ...prev };
              delete n[cellKey];
              return n;
            }),
          3000,
        );
      }
    } catch {
      setCellError((prev) => ({ ...prev, [cellKey]: "Network error" }));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenComments = (entry) => {
    if (!entry?.task?._id) return;
    setCommentModal({
      taskId: entry.task._id,
      taskName: entry.task?.name,
      commentCount: entry.commentCount || 0,
    });
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (isAuto = false) => {
    if (submitting || timesheetData?.status === "submitted") return;
    setSubmitting(true);
    try {
      let res, json;
      if (!timesheetData?._id) {
        // Create then submit
        const allEntries = (timesheetData?.projects || []).flatMap((p) =>
          (p.entries || []).map((e) => ({
            project: e.project?._id || e.project,
            task: e.task?._id || e.task,
            logs: (e.logs || []).map((l) => ({
              date: l.dateKey || toDateKey(l.date),
              hours: Number(l.hours) || 0,
              isLeave: l.isLeave || false,
            })),
          })),
        );
        res = await authFetch("/api/timesheets", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            startDate: startDateStr,
            endDate: toDateKey(new Date(monday.getTime() + 6 * 86400000)),
            entries: allEntries,
          }),
        });
        json = await res.json();
        if (!res.ok || !json?.data?._id) {
          setError(json?.message || "Failed to save.");
          return;
        }
        setTimesheetData(json.data);
        // Now submit
        res = await authFetch(`/api/timesheets/${json.data._id}/submit`, {
          method: "PATCH",
        });
        json = await res.json();
      } else {
        res = await authFetch(`/api/timesheets/${timesheetData._id}/submit`, {
          method: "PATCH",
        });
        json = await res.json();
      }
      if (res.ok && json?.data) {
        setTimesheetData(json.data);
        setSuccessMsg(
          isAuto
            ? "Timesheet auto-submitted."
            : "Timesheet submitted successfully!",
        );
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        setError(json?.message || "Submission failed.");
        setTimeout(() => setError(""), 3000);
      }
    } catch {
      setError("Network error.");
      setTimeout(() => setError(""), 3000);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────
  const isSubmitted = timesheetData?.status === "submitted";
  const weekDays = timesheetData?.weekDays || [];
  const projects = timesheetData?.projects || [];
  const hasProjects = projects.length > 0;

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-2 sm:p-4 md:p-6">
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-4 sm:px-6 pt-5 sm:pt-8 pb-5 sm:pb-8 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                Timesheet
              </h2>
              <p className="text-xs sm:text-sm text-gray-400 mt-0.5">
                Track And Manage Your Work Hours Seamlessly
              </p>
            </div>

            {/* Date range dropdown — pick any date, snaps to that week (Mon–Sun) */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowRangePicker((prev) => !prev)}
                className="flex items-center gap-2 h-9 px-3 border border-gray-200 rounded-lg text-xs sm:text-sm text-gray-700 hover:bg-gray-50 transition"
              >
                <Calendar size={14} className="text-gray-400" />
                {formatRangeLabel(monday, weekEnd)}
                <ChevronDown
                  size={14}
                  className={`text-gray-400 transition-transform ${
                    showRangePicker ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showRangePicker && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowRangePicker(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg">
                    <DatePicker
                      inline
                      selected={anchorDate}
                      onChange={(date) => {
                        setAnchorDate(date);
                        setShowRangePicker(false);
                      }}
                      calendarStartDay={1}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
            Loading timesheet...
          </div>
        ) : !hasProjects ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
            No tasks assigned to you yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse border-b">
              <thead>
                <tr className="border-b border-r border-gray-100">
                  <th className="text-left py-3 p-12 text-gray-500 font-medium text-[14px] border-r border-gray-100">
                    Project & Task
                  </th>
                  {weekDays.map((d, i) => {
                    const { day, weekday } = formatHeaderDate(d.date);
                    return (
                      <th key={i} className="px-3 py-3 text-center">
                        <div className="flex flex-col items-center leading-tight">
                          <span className="text-xs text-gray-400">
                            {day.split(" ")[0]} {day.split(" ")[1]}
                          </span>
                          <span className="text-sm font-semibold text-gray-700">
                            {weekday}
                          </span>
                        </div>
                      </th>
                    );
                  })}
                  <th className="px-4 py-3 text-left text-[#000000] font-medium text-[14px] border-l border-gray-100">
                    Comments
                  </th>
                </tr>
              </thead>
              <tbody>
                {projects.map((proj, pIdx) => (
                  <React.Fragment key={proj.project._id || pIdx}>
                    {/* Project name row */}
                    <tr>
                      <td className="px-6 pt-4 pb-1 border-r border-gray-100">
                        <span className="text-[16px] text-black">
                          {proj.project.name}
                        </span>
                      </td>
                      <td colSpan={weekDays.length} className="pt-4 pb-1" />
                      <td className="border-l border-gray-100" />
                    </tr>

                    {/* Task rows */}
                    {(proj.entries || []).map((entry, eIdx) => (
                      <tr
                        key={entry._id || eIdx}
                        className=" transition-colors"
                      >
                        <td className="px-6 py-2 text-gray-600 text-[12px] border-r border-gray-100">
                          {entry.task?.name}
                        </td>
                        {weekDays.map((day, dIdx) => {
                          const log =
                            (entry.logs || []).find(
                              (l) => l.dateKey === day.dateKey,
                            ) || {};
                          const cellKey = `${pIdx}-${eIdx}-${day.dateKey}`;
                          const isEdit = editingCell === cellKey;
                          const err = cellError[cellKey];

                          if (log.isLeave) {
                            return (
                              <td key={dIdx} className="px-3 py-2 text-center">
                                <span className="text-xs font-semibold text-amber-500 bg-amber-50 px-2 py-1 rounded">
                                  Leave
                                </span>
                              </td>
                            );
                          }

                          return (
                            <td
                              key={dIdx}
                              className="px-3 py-2 text-center relative"
                            >
                              {isEdit && !isSubmitted ? (
                                <input
                                  ref={focusEditInput}
                                  className="w-14 text-center border border-blue-400 rounded text-xs py-1 outline-none"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={() =>
                                    handleCellBlur(
                                      entry._id,
                                      day.dateKey,
                                      editValue,
                                      cellKey,
                                      pIdx,
                                      eIdx,
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") e.target.blur();
                                    if (e.key === "Escape")
                                      setEditingCell(null);
                                  }}
                                />
                              ) : (
                                <button
                                  onClick={() => {
                                    if (isSubmitted) return;
                                    setEditingCell(cellKey);
                                    setEditValue(
                                      Number(log.hours) > 0
                                        ? String(log.hours)
                                        : "",
                                    );
                                  }}
                                  className={`border rounded px-2 py-1 text-xs min-w-[44px] transition-colors ${
                                    Number(log.hours) > 0
                                      ? "border-blue-200 bg-blue-50 text-blue-700 font-medium"
                                      : "border-gray-200 text-gray-400"
                                  } ${isSubmitted ? "cursor-default" : "hover:border-blue-300 cursor-pointer"}`}
                                >
                                  {Number(log.hours) > 0
                                    ? `${Number(log.hours).toFixed(2)}`
                                    : "–"}
                                </button>
                              )}
                              {err && (
                                <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 z-10 bg-orange-50 border border-orange-200 text-orange-600 text-xs px-2 py-0.5 rounded whitespace-nowrap">
                                  {err}
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-4 py-2 border-l border-gray-100">
                          <button
                            onClick={() => handleOpenComments(entry)}
                            className="flex items-center gap-1 text-gray-400 hover:text-gray-600 transition text-xs"
                          >
                            <MessageSquare size={14} />
                            {entry.commentCount > 0
                              ? `${entry.commentCount} Comments`
                              : "Comment"}
                          </button>
                        </td>
                      </tr>
                    ))}

                    {/* Daily totals row */}
                    <tr className="border-t border-b border-gray-100 font-semibold ">
                      <td className="px-6 py-2 text-sm text-gray-700 border-r border-gray-100">
                        Total
                      </td>
                      {(proj.dailyTotals || []).map((t, i) => (
                        <td
                          key={i}
                          className="px-3 py-2 text-center text-xs text-gray-700"
                        >
                          {t.display === "Leave" ? (
                            <span className="text-amber-500 font-semibold">
                              Leave
                            </span>
                          ) : (
                            t.display
                          )}
                        </td>
                      ))}
                      <td className="border-l border-gray-100" />
                    </tr>
                    <tr>
                      <td className="py-2 border-r border-gray-100" />
                      <td colSpan={weekDays.length} className="py-2" />
                      <td className="py-2 border-l border-gray-100" />
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Notice cards */}
      {hasProjects && !loading && (
        <div className="flex flex-col sm:flex-row gap-4 mt-4">
          <div className="flex-1 h-[83px] bg-white rounded flex items-center justify-center gap-3">
            <Icons.Triangle />

            <span
              className="text-sm text-black/50 capitalize"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              You can update your entries anytime. Deleting entries is
              restricted.
            </span>
          </div>

          <div className="flex-1 h-[83px] bg-white rounded flex items-center justify-center gap-3">
            <Icons.Sheild />
            <span
              className="text-sm text-black/[.53] capitalize"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Once submitted, cannot be retrieved
            </span>
          </div>
        </div>
      )}

      {/* Submit row */}
      {hasProjects && !loading && (
        <div className="flex items-center justify-end gap-4 mt-4">
          {saving && <span className="text-xs text-gray-400">Saving...</span>}
          {error && (
            <span className="text-xs text-red-500 font-medium">{error}</span>
          )}
          {successMsg && (
            <span className="text-xs text-green-600 font-semibold">
              ✓ {successMsg}
            </span>
          )}
          {isSubmitted ? (
            <span className="px-5 py-2 bg-gray-100 text-gray-400 rounded-lg text-sm font-semibold cursor-not-allowed">
              Already Submitted
            </span>
          ) : (
            <button
              onClick={() => handleSubmit(false)}
              disabled={submitting}
              className="px-6 py-2 bg-[#4253C7] text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          )}
        </div>
      )}

      {/* Comment Modal */}
      {commentModal && (
        <CommentModal
          taskId={commentModal.taskId}
          taskName={commentModal.taskName}
          commentCount={commentModal.commentCount}
          isSubmitted={isSubmitted}
          onClose={() => setCommentModal(null)}
        />
      )}
    </div>
  );
};

export default Timesheet;
